import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Clock, Star } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { backendGet } from '../lib/backendClient';
import { matchRestaurantCategory, normalizeCategoryName } from '@/lib/categories';
import { DEFAULT_BANNER, resolveStoreCardImage } from '@/lib/storeImages';
import './RestaurantsByCategory.css';

const normalizeList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.restaurants)) return payload.restaurants;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
};

const getRestaurantRatingCount = (restaurant) => {
  const count = Number(restaurant?.rating_count);
  if (!Number.isFinite(count)) return null;
  return Math.max(0, Math.round(count));
};

const getRestaurantRatingValue = (restaurant) => {
  const rating = Number(restaurant?.rating);
  return Number.isFinite(rating) && rating > 0 ? rating : 0;
};

const formatRestaurantRating = (restaurant) => {
  const ratingValue = getRestaurantRatingValue(restaurant);
  const ratingCount = getRestaurantRatingCount(restaurant);
  if (ratingValue <= 0) return 'Novo';
  if (ratingCount === null) return ratingValue.toFixed(1).replace('.', ',');
  if (ratingCount <= 0) return 'Novo';
  return `${ratingValue.toFixed(1).replace('.', ',')} (${ratingCount})`;
};

export default function RestaurantsByCategory() {
  const { categoryName } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const selectedCategory = useMemo(
    () => normalizeCategoryName(decodeURIComponent(categoryName || '')),
    [categoryName]
  );

  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let isMounted = true;
    const loadStores = async () => {
      setLoading(true);
      setError('');
      try {
        const payload = await backendGet('/api/restaurants/', { skipAuth: !user });
        if (!isMounted) return;
        setRestaurants(normalizeList(payload));
      } catch (err) {
        if (!isMounted) return;
        setError(err?.message || 'Nao foi possivel carregar os estabelecimentos.');
        setRestaurants([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadStores();
    return () => {
      isMounted = false;
    };
  }, [user?.id]);

  const filteredRestaurants = useMemo(
    () => restaurants.filter((store) => matchRestaurantCategory(store?.categories || store?.category, selectedCategory)),
    [restaurants, selectedCategory]
  );

  const handleStoreClick = (store) => {
    if (!store?.id || store?.is_open === false) return;
    navigate(`/store/${store.id}`);
  };

  return (
    <div className="restaurants-by-category-page">
      <div className="restaurants-by-category-container">
        <button
          type="button"
          className="restaurants-by-category-back"
          onClick={() => navigate('/restaurantes')}
        >
          <ArrowLeft size={16} />
          Voltar
        </button>

        <header className="restaurants-by-category-header">
          <h1>{selectedCategory}</h1>
          <p>
            {loading
              ? 'Buscando estabelecimentos...'
              : `${filteredRestaurants.length} estabelecimento(s) encontrado(s) nessa categoria.`}
          </p>
        </header>

        {error ? (
          <div className="restaurants-by-category-feedback error">{error}</div>
        ) : null}

        {!loading && filteredRestaurants.length === 0 ? (
          <div className="restaurants-by-category-feedback empty">
            Nenhum estabelecimento encontrado para essa categoria na sua regiao.
          </div>
        ) : null}

        <section className="restaurants-section restaurants-by-category-list">
          <div className="restaurants-grid">
            {filteredRestaurants.map((restaurant) => (
              <div
                key={restaurant.id}
                className={`restaurant-card ${restaurant.is_open === false ? 'closed' : ''}`}
                onClick={() => handleStoreClick(restaurant)}
                style={{ cursor: restaurant.is_open === false ? 'not-allowed' : 'pointer' }}
              >
                <div className="restaurant-image">
                  <img
                    src={resolveStoreCardImage(restaurant)}
                    alt={restaurant.name || 'Restaurante'}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = DEFAULT_BANNER;
                    }}
                  />
                  {restaurant.discount ? <div className="discount-badge">{restaurant.discount}</div> : null}
                  {restaurant.is_open === false ? <div className="closed-badge">Fechado</div> : null}
                </div>

                <div className="restaurant-info">
                  <h3 className="restaurant-name">{restaurant.name}</h3>
                  <div className="restaurant-meta">
                    <div className="rating">
                      <Star size={14} fill="currentColor" />
                      <span>{formatRestaurantRating(restaurant)}</span>
                    </div>
                    <div className="delivery-time">
                      <Clock size={14} />
                      <span>{restaurant.delivery_time || '20-30 min'}</span>
                    </div>
                  </div>
                  <div className="restaurant-tags">
                    {restaurant.tags?.slice(0, 2).map((tag, index) => (
                      <span key={index} className="tag">
                        {tag}
                      </span>
                    ))}
                  </div>
                  <div className="delivery-fee">
                    Entrega: R$ {Number(restaurant.delivery_fee || 0).toFixed(2).replace('.', ',')}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
